import { routes } from "../appConfig/PermissionModule";
import moment from "moment";
import {} from "lucide-react";
import CircleCheck from "../components/ui/icons/CircleCheck";
/**
 * Format a date string or timestamp into a user-friendly format
 *
 * @param {string|number} dateInput - Date string or timestamp
 * @param {Object} options - Options for formatting
 * @returns {string} - Formatted date string
 */
export function formatDate(dateInput, options = {}) {
  if (!dateInput) return "";

  const date = new Date(dateInput);

  // Check if date is valid
  if (isNaN(date)) return "";

  // Default format: MMM DD, YYYY
  const {
    format = "medium",
    includeTime = false,
    timeFormat = "12h",
  } = options;

  const formatterOptions = {};

  switch (format) {
    case "short":
      formatterOptions.dateStyle = "short";
      break;
    case "medium":
      formatterOptions.dateStyle = "medium";
      break;
    case "long":
      formatterOptions.dateStyle = "long";
      break;
    case "full":
      formatterOptions.dateStyle = "full";
      break;
    default:
      formatterOptions.dateStyle = "medium";
  }

  if (includeTime) {
    formatterOptions.timeStyle = timeFormat === "24h" ? "medium" : "short";
  }

  return new Intl.DateTimeFormat("en-US", formatterOptions).format(date);
}

/**
 * Format a number as currency
 *
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} - Formatted currency string
 */
export function formatCurrency(amount, currency = "USD") {
  if (amount === null || amount === undefined) return "";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
}

/**
 * Format a number with thousands separators
 *
 * @param {number} number - The number to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted number
 */
export function formatNumber(number, options = {}) {
  if (number === null || number === undefined) return "";

  const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = options;

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(number);
}

/**
 * Convert camelCase to Title Case
 *
 * @param {string} text - The camelCase text to convert
 * @returns {string} - Title Case text
 */
export function camelToTitleCase(text) {
  if (!text) return "";

  // Add a space before each uppercase letter and then capitalize the first letter
  const result = text
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());

  return result;
}

/**
 * Truncate text to a specific length
 *
 * @param {string} text - The text to truncate
 * @param {number} length - Maximum length
 * @param {string} suffix - Suffix to add if truncated (default: "...")
 * @returns {string} - Truncated text
 */
export function truncateText(text, length = 50, suffix = "...") {
  if (!text || text.length <= length) return text;

  return text.substring(0, length).trim() + suffix;
}

/**
 * Generate a random color
 *
 * @param {number} index - Optional index for deterministic color generation
 * @returns {string} - Hex color code
 */
export function getRandomColor(index) {
  const colors = [
    "#4285F4", // Google Blue
    "#EA4335", // Google Red
    "#FBBC05", // Google Yellow
    "#34A853", // Google Green
    "#8E24AA", // Purple
    "#00ACC1", // Cyan
    "#FB8C00", // Orange
    "#607D8B", // Blue Grey
    "#1E88E5", // Light Blue
    "#43A047", // Light Green
  ];

  if (index !== undefined) {
    return colors[index % colors.length];
  }

  return colors[Math.floor(Math.random() * colors.length)];
}

/**
 * Calculate the time elapsed since a given date
 *
 * @param {string|number} dateInput - Date string or timestamp
 * @returns {string} - Elapsed time in human-readable format
 */
export function timeAgo(dateInput) {
  if (!dateInput) return "";

  const date = new Date(dateInput);

  // Check if date is valid
  if (isNaN(date)) return "";

  const now = new Date();
  const secondsAgo = Math.floor((now - date) / 1000);

  if (secondsAgo < 60) {
    return "just now";
  }

  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) {
    return `${minutesAgo} ${minutesAgo === 1 ? "minute" : "minutes"} ago`;
  }

  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) {
    return `${hoursAgo} ${hoursAgo === 1 ? "hour" : "hours"} ago`;
  }

  const daysAgo = Math.floor(hoursAgo / 24);
  if (daysAgo < 30) {
    return `${daysAgo} ${daysAgo === 1 ? "day" : "days"} ago`;
  }

  const monthsAgo = Math.floor(daysAgo / 30);
  if (monthsAgo < 12) {
    return `${monthsAgo} ${monthsAgo === 1 ? "month" : "months"} ago`;
  }

  const yearsAgo = Math.floor(monthsAgo / 12);
  return `${yearsAgo} ${yearsAgo === 1 ? "year" : "years"} ago`;
}

/**
 * Debounce a function call
 *
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait = 300) {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Generate a unique ID
 *
 * @returns {string} - Unique ID
 */
export function generateId() {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
}

/**
 * Check if a string is a valid email address
 *
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if valid
 */
export function isValidEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

/**
 * Group an array of objects by a specific property
 *
 * @param {Array} array - Array to group
 * @param {string} key - Property to group by
 * @returns {Object} - Grouped object
 */
export function groupBy(array, key) {
  return array.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
}

/**
 * Deep clone an object or array
 *
 * @param {any} obj - Object to clone
 * @returns {any} - Cloned object
 */
export function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Filter an array of objects based on search text
 *
 * @param {Array} array - Array to search
 * @param {string} searchText - Text to search for
 * @param {Array} fields - Fields to search in
 * @returns {Array} - Filtered array
 */
export function filterBySearchText(array, searchText, fields) {
  if (!searchText || !fields || fields.length === 0) {
    return array;
  }

  const lowerSearchText = searchText.toLowerCase();

  return array.filter((item) => {
    return fields.some((field) => {
      const value = item[field];
      if (value === null || value === undefined) return false;
      return String(value).toLowerCase().includes(lowerSearchText);
    });
  });
}

/**
 * Transform form data to API format
 *
 * @param {Object} formData - Form data
 * @returns {Object} - Transformed data
 */
export function transformFormData(formData) {
  // Remove any empty strings or null values
  const transformed = {};

  Object.entries(formData).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      transformed[key] = value;
    }
  });

  return transformed;
}

/**
 * Check if user has a specific permission
 *
 * @param {Object} user - User object
 * @param {string} permission - Permission to check
 * @returns {boolean} - True if user has permission
 */
export function hasPermissionToActionButton(role, currentPath) {
  const defaultPermission = {
    view: false,
    update: false,
    delete: false,
    add: false,
  };
  if (role.name === "admin") {
    return {
      view: true,
      update: true,
      delete: true,
      add: true,
    };
  }
  if (!role.permissions || role.permissions.length === 0) {
    return defaultPermission;
  }
  const currentModule = routes.find((route) => route.path === currentPath);
  if (currentModule) {
    const currentPermission = role.permissions.find((permission) => {
      return permission.key === currentModule.key;
    });
    currentPermission.scopes.map((scope) => {
      defaultPermission[scope.key] = scope.value;
    });
    return defaultPermission;
  } else return defaultPermission;
}

export function breakWord(word) {
  if (!word) return "-";

  let camelCaseSplit = word.replace(/([a-z])([A-Z])/g, "$1 $2");
  let result = camelCaseSplit.replace(/_/g, " ");

  result = result
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return result;
}

export const dataFormatter = (
  data,
  caseOptions = {
    true: (
      <div>
        Success <CircleCheck className="h-4 w-4 text-blue-500" />{" "}
      </div>
    ),
    false: (
      <div>
        <span>
          Failed <CircleCheck className="h-4 w-4 text-blue-500" />
        </span>
      </div>
    ),
  }
) => {
  if (data?.length === 0) return "Data Not Available";
  const dateregx =
    /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+)|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d)|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d)/;
  const isValid = String(data).match(dateregx);

  if (isValid) {
    if (moment(data).format("MMMM Do YYYY, h:mm:ss a") === "Invalid date") {
      return typeof data === "boolean" ? String(data) : data;
    }
    return moment(data).format("MMMM Do YYYY, h:mm:ss a");
  }
  if (typeof data === "boolean") {
    return caseOptions[String(data)];
  }
  return data;
};

export function generateSlug(text) {
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}
